import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { Eye } from 'lucide-react';
import { useAuth } from '@/lib/auth';
import { STATUS_CONFIG } from './utils';
import OrderPopup from './OrderPopup';
import BranchToggle from './BranchToggle';

export default function PartialPaidTab() {
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [viewOrder, setViewOrder] = useState(null);
  const [branches, setBranches] = useState([]);
  const [branchId, setBranchId] = useState('');
  const limit = 15;
  const isSA = user?.role === 'SUPER_ADMIN';

  useEffect(() => {
    if (isSA) api.get('/admin/branches').then(r => setBranches(r.data.data || [])).catch(() => {});
  }, [isSA]);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchInput.trim()), 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  useEffect(() => { setPage(1); }, [branchId, debouncedSearch]);

  const load = useCallback(() => {
    const params = new URLSearchParams();
    params.append('page', String(page));
    params.append('limit', String(limit));
    params.append('payment_status', 'partial');
    if (branchId) params.append('branch_id', branchId);
    if (debouncedSearch) params.append('q', debouncedSearch);
    api.get(`/admin/orders?${params.toString()}`).then(r => {
      const d = r.data.data || {};
      setOrders(d.orders || []);
      setTotal(d.total || 0);
    }).catch(() => {});
  }, [page, debouncedSearch, branchId]);

  useEffect(() => { load(); }, [load]);

  const totalPages = Math.max(1, Math.ceil(total / limit));

  const openView = async (id) => {
    try { const r = await api.get(`/admin/orders/${id}`); setViewOrder(r.data.data); }
    catch { toast.error('Failed'); }
  };

  return (
    <div data-testid="partial-paid-tab">
      <div className="mb-4 p-3 rounded-lg bg-amber-50 border border-amber-200 text-xs text-amber-800">
        Orders where the customer has paid <strong>some but not all</strong> of the total amount. Sorted latest-first.
      </div>
      <div className="flex flex-wrap items-center gap-3 mb-4">
        {isSA && branches.length > 0 && (
          <BranchToggle branches={branches} value={branchId} onChange={setBranchId} testIdPrefix="partial-branch" />
        )}
        <Input
          value={searchInput}
          onChange={e => setSearchInput(e.target.value)}
          placeholder="Search order number, name, phone..."
          className="max-w-sm"
          data-testid="partial-paid-search"
        />
        <span className="text-xs text-muted-foreground ml-auto" data-testid="partial-paid-total">
          {total} {total === 1 ? 'result' : 'results'}
        </span>
      </div>

      {/* Column header */}
      {orders.length > 0 && (
        <div className="hidden sm:grid grid-cols-12 items-center text-[10px] font-semibold text-muted-foreground uppercase tracking-wider px-4 py-1 mb-1">
          <div className="col-span-4">Order</div>
          <div className="col-span-2 text-right tabular-nums">Total</div>
          <div className="col-span-2 text-right tabular-nums">Paid</div>
          <div className="col-span-2 text-right tabular-nums">Balance</div>
          <div className="col-span-2 text-right">Action</div>
        </div>
      )}

      <div className="space-y-2">
        {orders.map(order => {
          const cfg = STATUS_CONFIG[order.status] || { label: order.status, color: 'bg-muted' };
          const balance = Math.max(0, (order.total_amount || 0) - (order.amount_paid || 0));
          return (
            <div key={order.id} className="grid grid-cols-1 sm:grid-cols-12 items-center border rounded-xl px-4 py-3 gap-2" data-testid={`partial-order-${order.id}`}>
              <div className="sm:col-span-4">
                <p className="text-sm font-bold">{order.order_number}</p>
                <p className="text-xs text-muted-foreground truncate">
                  {order.customer_name || '-'} &middot; {order.category_name || '-'} &middot; <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${cfg.color}`}>{cfg.label}</span>
                </p>
              </div>
              <div className="sm:col-span-2 sm:text-right"><span className="text-xs text-muted-foreground sm:hidden">Total: </span><span className="font-semibold tabular-nums">Rs. {order.total_amount || 0}</span></div>
              <div className="sm:col-span-2 sm:text-right"><span className="text-xs text-muted-foreground sm:hidden">Paid: </span><span className="font-semibold text-green-700 tabular-nums">Rs. {order.amount_paid || 0}</span></div>
              <div className="sm:col-span-2 sm:text-right"><span className="text-xs text-muted-foreground sm:hidden">Balance: </span><span className="font-semibold text-amber-700 tabular-nums" data-testid={`partial-balance-${order.id}`}>Rs. {balance}</span></div>
              <div className="sm:col-span-2 sm:text-right">
                <Button size="sm" variant="outline" className="text-xs h-7 gap-1" onClick={() => openView(order.id)} data-testid={`view-partial-${order.id}`}>
                  <Eye className="w-3 h-3" /> View
                </Button>
              </div>
            </div>
          );
        })}
        {orders.length === 0 && <p className="text-sm text-muted-foreground py-6 text-center">No partial-paid orders.</p>}
      </div>

      {total > 0 && (
        <div className="flex items-center justify-center gap-2 mt-4 pt-3 border-t border-border/50">
          <Button variant="outline" size="sm" disabled={page === 1} onClick={() => setPage(p => p - 1)} className="text-xs h-7" data-testid="partial-prev-btn">Prev</Button>
          <span className="text-xs text-muted-foreground tabular-nums" data-testid="partial-pagination">Page {page} of {totalPages}</span>
          <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} className="text-xs h-7" data-testid="partial-next-btn">Next</Button>
        </div>
      )}

      {viewOrder && <OrderPopup order={viewOrder} onClose={() => setViewOrder(null)} onRefresh={load} role={user?.role} />}
    </div>
  );
}
